import { useAuth0 } from "@auth0/auth0-react";
import { DeleteOutline, EditOutlined, MoreHoriz } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, Stack, Typography } from "@mui/material";
import copy from "fast-copy";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { TextOnlyEditor } from "../../HOCs";
import { Association } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { IComment, useCreateCommentMutation, useDeleteCommentMutation, useLazyGetCommentsQuery, useUpdateCommentMutation } from "../../redux/services";
import { closeConfirmationDialog, openConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { allSunEditorPlugins, togglePlugin } from "../../utils";
import { UserAvatar } from "../userAvatar";

export const textEditorButtonList = [
    ["undo", "redo"],
    ["formatBlock"],
    ["bold", "underline", "italic", "strike", "subscript", "superscript", "list"],
    ["fullScreen"]
];

export const allEditorButtonList = [
    ["undo", "redo"],
    ["formatBlock"],
    ["bold", "underline", "italic", "strike", "subscript", "superscript", "list"],
    ["removeFormat"],
    ["fontColor", "hiliteColor"],
    ["outdent", "indent"],
    ["align", "horizontalRule", "table"],
    ["link", "image"],
    ["fullScreen"]
];

type EditorState = "hidden" | "expanded" | "reset";

export const CommentSection: React.FC<{ objectId: string, objectType: Association['object_type'], userId?: string }> = ({ objectId, objectType, userId }) => {

    const { user } = useAuth0();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [getComments, getCommentsData] = useLazyGetCommentsQuery();
    const [createComment, createCommentData] = useCreateCommentMutation();
    const [updateComment] = useUpdateCommentMutation();
    const [deleteComment] = useDeleteCommentMutation();

    const [comments, setComments] = React.useState<IComment[]>([]);
    const [newComment, setNewComment] = React.useState<string>("");
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [currentComment, setCurrentComment] = React.useState<{ comment: IComment, index: number } | undefined>();
    const [editMode, setEditMode] = React.useState<boolean>(false);
    const [editorCreateMode, setCreateMode] = React.useState<EditorState>("hidden");
    const [editorUpdateMode, setUpdateMode] = React.useState<EditorState>("hidden");

    const open = Boolean(anchorEl);

    const handleCommentMenuClick = (event: React.MouseEvent<HTMLButtonElement>, comment: IComment, index: number) => {
        setAnchorEl(event.currentTarget);
        setCurrentComment({ comment, index });
        setEditMode(false);
    };

    const handleCommentMenuClose = () => {
        setAnchorEl(null);
    };

    const fetchComments = async () => {
        const commentsData = await getComments({ parent_object_id: objectId, parent_object_type: objectType, user_id: userId, sort_by: "created_time", sort_order: 'asc' }).unwrap();
        setComments(copy(commentsData.data));
    };

    const addNewComment = async () => {
        setCreateMode("reset");
        if (newComment.length) {
            const response = await createComment({ title: user?.name ?? "", content: newComment, parent: { object_id: objectId, object_type: objectType } }).unwrap();

            if (response._id) {
                enqueueSnackbar('Comment added successfully!', { variant: "success" });
                setNewComment('');
                setCreateMode("hidden");
                fetchComments();
            } else {
                enqueueSnackbar('Something went wrong, Unable to add comment', { variant: "error" });
            }
        }
    };

    const handleDelCommentBtn = () => {
        handleCommentMenuClose();

        if (!currentComment) {
            return false;
        }

        const { comment, index } = currentComment;

        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: "Do you want delete the comment?",
            positiveBtn: "Delete",
            onOk: async () => {
                dispatch(closeConfirmationDialog())
                deleteComment(comment._id).unwrap().catch(err => {
                    console.log('err:', err);
                    enqueueSnackbar(err.data?.title ?? `Something went wrong, Unable to delete.`, { variant: 'error' });
                });

                let newComments = comments;
                newComments.splice(index, 1);
                setComments([...newComments]);
                setCurrentComment(undefined);
            },
            onNegativeBtn: () => {
                dispatch(closeConfirmationDialog())
            }
        }))
    };

    const handleEditCommentBtn = () => {
        handleCommentMenuClose();

        if (!currentComment) {
            return false;
        }

        setEditMode(true);
    };

    const updateCurrentComment = () => {
        if (currentComment) {
            let newComments = comments;
            newComments[currentComment.index] = currentComment?.comment;
            setComments([...newComments]);

            updateComment({ id: currentComment.comment._id, payload: currentComment.comment }).unwrap().catch(err => {
                console.log('err:', err);
                enqueueSnackbar(err.data?.title ?? `Something went wrong, Unable to update comment.`, { variant: 'error' });
            });
        }
        setCurrentComment(undefined)
        setEditMode(false);
    };

    React.useEffect(() => {
        fetchComments();
        // eslint-disable-next-line
    }, [objectId, objectType, userId]);

    return <Stack gap={2}>

        {comments.length > 0 && <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }} gutterBottom>Comments</Typography>}

        {comments.map((comment, index) => <Stack key={comment._id} direction={"row"} gap={2}>
            {/* Header */}
            <UserAvatar userId={`${comment?.created_user_id ?? ''}`} name={`${comment?.created_user ?? ""}`} avatarUrl="" />

            <Stack sx={{ width: "100%" }}>
                <Stack
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        p: 1,
                        width: "100%",
                        borderTopRightRadius: '8px',
                        borderTopLeftRadius: '8px',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderBottomWidth: 0
                    }}
                    direction={"row"}
                    justifyContent={"space-between"}
                >
                    <Stack>
                        <Typography variant="body2">{comment.title}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            Commented on <span style={{ fontFamily: "htrts_medium" }}>{moment(comment.created_time).format('lll')}&nbsp;</span>
                            {comment.modified_time && <>last edited by <span style={{ fontFamily: "htrts_medium" }}>{comment.modified_user}&nbsp;</span>
                                on <span style={{ fontFamily: "htrts_medium" }}>{moment(comment.modified_time).format('lll')}&nbsp;</span></>}
                        </Typography>
                    </Stack>

                    <IconButton onClick={(e) => handleCommentMenuClick(e, comment, index)}>
                        <MoreHoriz />
                    </IconButton>
                </Stack>

                <Stack gap={1} sx={{ minHeight: '16px' }}>
                    {/* Read Only Comment */}
                    {(currentComment?.comment?._id !== comment._id || !editMode) && <TextOnlyEditor
                        height="fit-content; padding: 12px; min-height: unset; padding-bottom: 8px;"
                        disable={true}
                        setOptions={{
                            value: comment.content,
                            hideToolbar: true,
                            charCounter: false,
                            resizeEnable: false,
                            resizingBar: false,
                            buttonList: textEditorButtonList,
                        }}
                    />
                    }

                    {/* Edit Comment */}
                    {(currentComment?.comment?._id === comment._id && editMode) && <Stack gap={1}>
                        <TextOnlyEditor
                            key={editorUpdateMode}
                            placeholder="Enter Comment"
                            onChange={(newComment: string) => setCurrentComment({
                                ...currentComment,
                                comment: { ...currentComment.comment, content: newComment }
                            })}
                            height="200px"
                            setOptions={{
                                value: currentComment?.comment?.content ?? "",
                                charCounterType: "char",
                                maxCharCount: 5000,
                                defaultStyle: "font-family: 'hrts_regular'",
                                plugins: [...allSunEditorPlugins, togglePlugin(() => setUpdateMode(prev => prev === "expanded" ? "hidden" : "expanded"), editorUpdateMode === "expanded")],
                            }}
                            getSunEditorInstance={(instance) => {
                                // Optionally force the toolbar buttons based on current state.
                                if (editorUpdateMode === "expanded") {
                                    instance.setToolbarButtons([...allEditorButtonList, ["toggleButton"]]);
                                } else {
                                    instance.setToolbarButtons([...textEditorButtonList, ["toggleButton"]]);
                                }
                            }}
                        />

                        {/* Footer Button */}
                        <Stack direction={"row"} justifyContent={"flex-end"} gap={1}>
                            <Button variant="outlined" onClick={() => {
                                setCurrentComment(undefined);
                                setEditMode(false);
                            }}>Cancel</Button>
                            <LoadingButton variant="contained" onClick={() => updateCurrentComment()}>Update</LoadingButton>
                        </Stack>
                    </Stack>}
                </Stack>
            </Stack>
        </Stack>)}

        {(getCommentsData.isLoading || getCommentsData.isFetching) && <>
            {[1].map(task => <Stack key={task} direction={"row"} gap={2}>
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="rounded" width={"100%"} height={'200px'} />
            </Stack>)}
        </>}

        <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }} gutterBottom>Add your comment</Typography>

        {/* Add Comment */}
        <Stack direction={"row"} gap={2}>
            <UserAvatar userId={`${user?.sub ?? ''}`} name={`${user?.name ?? ""}`} avatarUrl={""} />

            {!createCommentData.isLoading && <TextOnlyEditor
                key={editorCreateMode}
                placeholder="Enter Comment"
                onChange={(newComment: string) => setNewComment(newComment)}
                height="200px"
                setOptions={{
                    value: newComment,
                    charCounterType: "char",
                    maxCharCount: 5000,
                    defaultStyle: "font-family: 'hrts_regular'",
                    plugins: [...allSunEditorPlugins, togglePlugin(() => setCreateMode(prev => prev === "expanded" ? "hidden" : "expanded"), editorCreateMode === "expanded")],

                }}
                disable={createCommentData.isLoading}
                getSunEditorInstance={(instance) => {
                    // Optionally force the toolbar buttons based on current state.
                    if (editorCreateMode === "expanded") {
                        instance.setToolbarButtons([...allEditorButtonList, ["toggleButton"]]);
                    } else {
                        instance.setToolbarButtons([...textEditorButtonList, ["toggleButton"]]);
                    }
                }}
            />}

            {createCommentData.isLoading && <Skeleton variant="rounded" width={"100%"} height={'200px'} />}
        </Stack>

        {createCommentData.isError && <Alert variant="outlined" severity="error">
            {`Something went wrong, Unable to comment`}
        </Alert>}

        {/* Footer Button */}
        <Stack direction={"row"} justifyContent={"flex-end"} gap={1}>
            <Button disabled={createCommentData.isLoading} variant="outlined" >Cancel</Button>
            <LoadingButton loading={createCommentData.isLoading} variant="contained" onClick={() => addNewComment()} >Comment</LoadingButton>
        </Stack>

        {/* Comment Menu */}
        <Menu
            id="comment-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleCommentMenuClose}
        >
            <MenuItem onClick={() => handleEditCommentBtn()}>
                <ListItemIcon>
                    <EditOutlined color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleDelCommentBtn()}>
                <ListItemIcon>
                    <DeleteOutline color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
            </MenuItem>
        </Menu>
    </Stack >
}